// Draw-once signature capture for order paperwork.
//
// Two ways in, one way out: whichever mode you use, the pad produces a single
// PNG data URL, because the backend stamps an image and knows nothing about
// how it was made. Drawing is the default — a typed name in a cursive face is
// the keyboard-only fallback, reusing the font stack SignAndSaveModal already
// uses for signed notes so the two features look like one product.
//
// "Save as my signature" is ticked by default: the whole point is that the
// second document you sign is one click.
import React, { useCallback, useEffect, useRef, useState } from "react";

const CURSIVE = '"Segoe Script","Bradley Hand","Snell Roundhand",cursive';

const CANVAS_W = 600;
const CANVAS_H = 200;

export default function SignaturePad({
  initialPng = null,
  signerName = "",
  busy = false,
  onSave,
  onCancel,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [mode, setMode] = useState("draw"); // "draw" | "type"
  const [typed, setTyped] = useState("");
  const [hasInk, setHasInk] = useState(false);
  const [remember, setRemember] = useState(true);
  const [name, setName] = useState(signerName || "");

  const ctx = useCallback(() => {
    const c = canvasRef.current;
    return c ? c.getContext("2d") : null;
  }, []);

  const clear = useCallback(() => {
    const g = ctx();
    if (g) g.clearRect(0, 0, CANVAS_W, CANVAS_H);
    setHasInk(false);
    setTyped("");
  }, [ctx]);

  // Render the typed name onto the SAME canvas the pointer draws on, so both
  // modes converge on one toDataURL() and the caller never branches.
  useEffect(() => {
    if (mode !== "type") return;
    const g = ctx();
    if (!g) return;
    g.clearRect(0, 0, CANVAS_W, CANVAS_H);
    if (!typed.trim()) return;
    g.fillStyle = "#111827";
    g.font = `64px ${CURSIVE}`;
    g.textBaseline = "middle";
    g.fillText(typed.trim(), 24, CANVAS_H / 2);
  }, [mode, typed, ctx]);

  const pointFrom = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((e.clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  const startDraw = (e) => {
    if (mode !== "draw") return;
    e.preventDefault();
    const g = ctx();
    if (!g) return;
    drawingRef.current = true;
    g.strokeStyle = "#111827";
    g.lineWidth = 4;
    g.lineCap = "round";
    g.lineJoin = "round";
    const p = pointFrom(e);
    g.beginPath();
    g.moveTo(p.x, p.y);
  };

  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    const g = ctx();
    if (!g) return;
    const p = pointFrom(e);
    g.lineTo(p.x, p.y);
    g.stroke();
    setHasInk(true);
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const ready = mode === "draw" ? hasInk : Boolean(typed.trim());

  const handleSave = () => {
    const c = canvasRef.current;
    if (!c) return;
    onSave?.({
      png: c.toDataURL("image/png"),
      signerName: (mode === "type" && typed.trim()) || name.trim() || signerName,
      remember,
    });
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">✍️ Your signature</h3>
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => { setMode("draw"); clear(); }}
            className={`px-2 py-1 rounded ring-1 ${
              mode === "draw"
                ? "bg-slate-700 text-white ring-slate-700"
                : "ring-slate-300 dark:ring-slate-600 text-slate-600 dark:text-slate-300"
            }`}
          >
            Draw
          </button>
          <button
            type="button"
            onClick={() => { setMode("type"); setHasInk(false); }}
            className={`px-2 py-1 rounded ring-1 ${
              mode === "type"
                ? "bg-slate-700 text-white ring-slate-700"
                : "ring-slate-300 dark:ring-slate-600 text-slate-600 dark:text-slate-300"
            }`}
          >
            Type instead
          </button>
        </div>
      </div>

      {/* Actions sit ABOVE the pad: they used to be under the canvas, the
          name field and the checkbox, so on a short window the way to finish
          was off-screen. */}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!ready || busy}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm disabled:bg-slate-200 disabled:text-slate-600 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
        >
          {busy ? "Saving…" : "Save signature"}
        </button>
        {initialPng && (
          <button
            type="button"
            onClick={() => onSave?.({ useSaved: true, signerName: name.trim() || signerName })}
            disabled={busy}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600 disabled:opacity-50"
          >
            Use saved signature
          </button>
        )}
        <button
          type="button"
          onClick={clear}
          className="text-xs px-3 py-1.5 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
        >
          Cancel
        </button>
      </div>

      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        aria-label="Signature pad"
        onPointerDown={startDraw}
        onPointerMove={moveDraw}
        onPointerUp={endDraw}
        onPointerLeave={endDraw}
        className={`w-full rounded-md bg-white border border-dashed border-slate-300 ${
          mode === "draw" ? "cursor-crosshair" : "cursor-default"
        }`}
        style={{ touchAction: "none", aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}
      />

      {mode === "type" && (
        <div className="mt-3">
          <label htmlFor="typed-signature" className="text-xs font-semibold">
            Type your signature
          </label>
          <input
            id="typed-signature"
            aria-label="Type your signature"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Dr. Jane Lee"
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1 text-sm"
          />
        </div>
      )}

      <div className="mt-3">
        <label htmlFor="signer-name" className="text-xs font-semibold">
          Printed name
        </label>
        <input
          id="signer-name"
          aria-label="Printed name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Dr. Jane Lee"
          className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1 text-sm"
        />
        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
          Printed under the mark, with the date, on every document you sign.
        </p>
      </div>

      <label className="mt-3 flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          aria-label="Save as my signature"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
        />
        Save as my signature, so the next document is one click
      </label>

    </div>
  );
}
